/**
 * Cashflow Zustand Store
 *
 * State management for the Cashflow calendar feature.
 * Handles entries, AI parsing, and UI state.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { cashflow, creditCards, spendingAnalysis } from '../services/supabaseService';
import { parseCashflowCommand, parsePaymentSchedule, parsePaymentScheduleFromImage, parseCreditCardStatement, parseCreditCardStatementFromImage, analyzeSpending } from '../services/geminiService';
import { extractTextFromExcel, isExcel } from '../services/excelService';
import { extractTextFromPDF, isPDF } from '../services/pdfService';
import { fetchExchangeRates, convertToTRY } from '../services/exchangeRateService';

// Helper: Check if file is an image
const isImage = (file) => {
  const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
  return imageTypes.includes(file.type);
};

// Helper: Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove the data URL prefix to get just the base64 data
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};
import {
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  format,
  isBefore,
  startOfDay,
} from 'date-fns';

// Helper: Calculate next occurrence for recurring entries
const calculateNextOccurrence = (entry, afterDate = new Date()) => {
  if (!entry.is_recurring || !entry.recurring_rule) return null;

  const rule = entry.recurring_rule;
  const startDate = new Date(entry.date);
  let nextDate = new Date(startDate);

  while (isBefore(nextDate, afterDate) || nextDate <= afterDate) {
    switch (rule.frequency) {
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7 * (rule.interval || 1));
        break;
      case 'biweekly':
        nextDate.setDate(nextDate.getDate() + 14);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + (rule.interval || 1));
        if (rule.dayOfMonth) {
          nextDate.setDate(Math.min(rule.dayOfMonth, new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate()));
        }
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + (rule.interval || 1));
        break;
      default:
        return null;
    }

    // Check end date
    if (rule.endDate && nextDate > new Date(rule.endDate)) {
      return null;
    }
  }

  return nextDate;
};

// Helper: Get last day of a month
const getLastDayOfMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate();
};

// Helper: Get the Nth occurrence of a day of week in a given month
// weekOfMonth: 1 = first, 2 = second, 3 = third, 4 = fourth
// dayOfWeek: 0 = Sunday, 1 = Monday, ... 5 = Friday, 6 = Saturday
const getNthDayOfWeekInMonth = (year, month, dayOfWeek, weekOfMonth) => {
  // Start from the 1st of the month
  const date = new Date(year, month, 1);

  // Find the first occurrence of the target day of week
  const firstDayOfWeek = date.getDay();
  let daysUntilTarget = dayOfWeek - firstDayOfWeek;
  if (daysUntilTarget < 0) daysUntilTarget += 7;

  // First occurrence is on this day
  const firstOccurrence = 1 + daysUntilTarget;

  // Calculate the Nth occurrence (add 7 days for each additional week)
  const nthOccurrence = firstOccurrence + (weekOfMonth - 1) * 7;

  // Make sure the date is still in the same month
  const lastDay = getLastDayOfMonth(year, month);
  if (nthOccurrence > lastDay) {
    // If requested week doesn't exist (e.g., 5th Friday), return last occurrence
    return firstOccurrence + Math.floor((lastDay - firstOccurrence) / 7) * 7;
  }

  return nthOccurrence;
};

// Helper: Advance date by frequency, handling month-end correctly
const advanceByFrequency = (date, rule) => {
  const newDate = new Date(date);

  switch (rule.frequency) {
    case 'weekly':
      newDate.setDate(newDate.getDate() + 7 * (rule.interval || 1));
      break;
    case 'biweekly':
      newDate.setDate(newDate.getDate() + 14);
      break;
    case 'monthly': {
      // For monthly, we need to handle day of month carefully
      const targetMonth = newDate.getMonth() + (rule.interval || 1);
      newDate.setDate(1); // Set to 1st to avoid overflow issues
      newDate.setMonth(targetMonth);

      if (rule.isLastDayOfMonth) {
        // Set to last day of the new month
        newDate.setDate(getLastDayOfMonth(newDate.getFullYear(), newDate.getMonth()));
      } else if (rule.weekOfMonth !== undefined && rule.dayOfWeek !== undefined) {
        // "Nth day of week" pattern (e.g., first Friday of month)
        const nthDay = getNthDayOfWeekInMonth(
          newDate.getFullYear(),
          newDate.getMonth(),
          rule.dayOfWeek,
          rule.weekOfMonth
        );
        newDate.setDate(nthDay);
      } else if (rule.dayOfMonth) {
        // Set to specific day, capped at month's last day
        const lastDay = getLastDayOfMonth(newDate.getFullYear(), newDate.getMonth());
        newDate.setDate(Math.min(rule.dayOfMonth, lastDay));
      }
      break;
    }
    case 'yearly':
      newDate.setFullYear(newDate.getFullYear() + (rule.interval || 1));
      if (rule.isLastDayOfMonth) {
        newDate.setDate(getLastDayOfMonth(newDate.getFullYear(), newDate.getMonth()));
      }
      break;
  }

  return newDate;
};

// Helper: Generate virtual entries for recurring items in a date range
const generateRecurringInstances = (parentEntry, startDate, endDate) => {
  if (!parentEntry.is_recurring || !parentEntry.recurring_rule) return [];

  const instances = [];
  const rule = parentEntry.recurring_rule;
  let currentDate = new Date(parentEntry.date);

  // Move to first occurrence in range
  while (isBefore(currentDate, startDate)) {
    currentDate = advanceByFrequency(currentDate, rule);
  }

  // Generate instances in range
  while (currentDate <= endDate) {
    if (rule.endDate && currentDate > new Date(rule.endDate)) break;

    instances.push({
      ...parentEntry,
      id: `virtual_${parentEntry.id}_${format(currentDate, 'yyyy-MM-dd')}`,
      date: format(currentDate, 'yyyy-MM-dd'),
      recurring_parent_id: parentEntry.id,
      is_virtual: true,
      status: 'pending',
    });

    currentDate = advanceByFrequency(currentDate, rule);
  }

  return instances;
};

export const useCashflowStore = create(
  subscribeWithSelector((set, get) => ({
    // =====================================================
    // STATE
    // =====================================================

    // Entries
    entries: [],
    recurringParents: [],
    isLoading: false,
    error: null,

    // View state
    currentMonth: new Date(),
    viewMode: 'calendar', // 'calendar' | 'list'

    // Modal state
    isAIModalOpen: false,
    isEditModalOpen: false,
    isPreviewModalOpen: false,
    selectedEntry: null,
    parsedEntry: null,

    // AI state
    isParsingCommand: false,
    parseError: null,
    aiCommand: '',

    // Payment schedule import state
    isScheduleModalOpen: false,
    isParsingSchedule: false,
    parsedSchedule: null,
    scheduleParseError: null,
    selectedPayments: [], // IDs of payments selected for import

    // Overdue
    overdueEntries: [],
    isOverduePanelOpen: false,

    // Past debts (from previous months)
    pastDebts: [],
    pastDebtsTotal: 0,
    pastDebtsTotalTRY: 0,
    pastDebtsByCurrency: {},

    // Summary
    summary: {
      totalIncome: 0,
      totalExpense: 0,
      netAmount: 0,
      pendingCount: 0,
      overdueCount: 0,
      completedCount: 0,
      byCurrency: {},
      // TRY equivalents (converted using exchange rates)
      totalIncomeTRY: 0,
      totalExpenseTRY: 0,
      netAmountTRY: 0,
    },

    // Exchange rates
    exchangeRates: null,
    isLoadingRates: false,

    // Category filters (format: "category:type" e.g. "salary:income", "rent:expense")
    visibleFilters: new Set(), // Empty means all visible
    availableFilters: [], // Category-type combinations present in current month

    // Credit card statement state
    isCreditCardModalOpen: false,
    isCreditCardSidebarOpen: false,
    isParsingStatement: false,
    parsedStatement: null,
    statementParseError: null,
    selectedTransactions: [], // tempIds of transactions selected for import
    creditCardStatements: [],
    creditCardTransactions: [],
    isLoadingCreditCards: false,

    // Spending analysis state
    isSpendingAnalysisOpen: false,
    spendingAnalysis: null,
    spendingAnalysisId: null, // DB record id
    spendingAnalysisUpdatedAt: null,
    isAnalyzingSpending: false,
    spendingAnalysisError: null,
    isLoadingCachedAnalysis: false,

    // Credit card payment mode toggle
    // 'minimum' = show minimum payments in cashflow
    // 'full' = show full balance in cashflow
    creditCardPaymentMode: 'minimum',

    // =====================================================
    // NAVIGATION ACTIONS
    // =====================================================

    setCurrentMonth: (month) => set({ currentMonth: month }),

    goToPreviousMonth: () => {
      const { currentMonth } = get();
      set({ currentMonth: subMonths(currentMonth, 1) });
      get().loadEntries();
    },

    goToNextMonth: () => {
      const { currentMonth } = get();
      set({ currentMonth: addMonths(currentMonth, 1) });
      get().loadEntries();
    },

    goToToday: () => {
      set({ currentMonth: new Date() });
      get().loadEntries();
    },

    setViewMode: (mode) => set({ viewMode: mode }),

    // =====================================================
    // DATA LOADING ACTIONS
    // =====================================================

    loadEntries: async () => {
      const { currentMonth, exchangeRates, creditCardPaymentMode } = get();
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

      set({ isLoading: true, error: null });

      try {
        // Fetch exchange rates if not cached
        let rates = exchangeRates;
        if (!rates) {
          set({ isLoadingRates: true });
          rates = await fetchExchangeRates();
          set({ exchangeRates: rates, isLoadingRates: false });
        }

        // Load regular entries
        let entries = await cashflow.getEntries(start, end);

        // Load credit card statements to get minimum payment info
        const statements = await creditCards.getStatements();

        // Update store with statements so toggle button can appear
        if (statements.length > 0) {
          set({ creditCardStatements: statements });
        }

        const statementMap = new Map();
        statements.forEach(stmt => {
          // Map by statement_id for direct lookup
          statementMap.set(stmt.id, stmt);
          // Also map by due_date + approximate amount for legacy entries
          const key = `${stmt.due_date}_${Math.round(stmt.total_balance)}`;
          statementMap.set(key, stmt);
        });

        // Adjust credit card entry amounts based on payment mode
        entries = entries.map(entry => {
          // Check if this is a credit card entry (either new format with metadata or old format with category)
          const isNewFormat = entry.metadata?.is_credit_card_payment;
          const isOldFormat = entry.category === 'credit_card' && !isNewFormat;

          if (isNewFormat) {
            // New format: metadata has both amounts
            const targetAmount = creditCardPaymentMode === 'minimum'
              ? entry.metadata.minimum_payment
              : entry.metadata.full_balance;

            if (targetAmount && targetAmount !== parseFloat(entry.amount)) {
              return {
                ...entry,
                amount: targetAmount,
                _originalAmount: entry.amount,
                _minimumPayment: entry.metadata.minimum_payment,
                _fullBalance: entry.metadata.full_balance,
                _paymentMode: creditCardPaymentMode,
              };
            }
          } else if (isOldFormat) {
            // Old format: try to find matching statement
            const statementId = entry.metadata?.statement_id;
            let stmt = statementId ? statementMap.get(statementId) : null;

            // Fallback: try to match by due_date and amount
            if (!stmt) {
              const key = `${entry.date}_${Math.round(parseFloat(entry.amount))}`;
              stmt = statementMap.get(key);
            }

            if (stmt && stmt.minimum_payment) {
              const minimumPayment = parseFloat(stmt.minimum_payment);
              const fullBalance = parseFloat(stmt.total_balance);
              const targetAmount = creditCardPaymentMode === 'minimum' ? minimumPayment : fullBalance;

              return {
                ...entry,
                amount: targetAmount,
                _originalAmount: entry.amount,
                _minimumPayment: minimumPayment,
                _fullBalance: fullBalance,
                _paymentMode: creditCardPaymentMode,
                _statementId: stmt.id,
              };
            }
          }

          return entry;
        });

        // Load recurring parents to generate virtual instances
        const recurringParents = await cashflow.getRecurringParents();

        // Generate virtual instances for recurring entries
        const startDate = startOfMonth(currentMonth);
        const endDate = endOfMonth(currentMonth);

        let allEntries = [...entries];

        recurringParents.forEach(parent => {
          const virtualInstances = generateRecurringInstances(parent, startDate, endDate);

          // Filter out virtual instances that already have real entries
          // Also include parent's own date to avoid duplicate with parent entry
          const existingDates = entries
            .filter(e => e.recurring_parent_id === parent.id || e.id === parent.id)
            .map(e => e.date);

          const newVirtuals = virtualInstances.filter(
            v => !existingDates.includes(v.date)
          );

          allEntries = [...allEntries, ...newVirtuals];
        });

        // Sort by date
        allEntries.sort((a, b) => new Date(a.date) - new Date(b.date));

        // Mark overdue entries
        const today = startOfDay(new Date());
        allEntries = allEntries.map(entry => {
          if (entry.status === 'pending' && new Date(entry.date) < today) {
            return { ...entry, status: 'overdue' };
          }
          return entry;
        });

        // Extract available category-type combinations from entries
        const filterSet = new Set(allEntries.map(e => `${e.category || 'other'}:${e.type}`));
        const availableFilters = Array.from(filterSet).sort((a, b) => {
          // Sort by type first (income before expense), then by category
          const [catA, typeA] = a.split(':');
          const [catB, typeB] = b.split(':');
          if (typeA !== typeB) return typeA === 'income' ? -1 : 1;
          return catA.localeCompare(catB);
        });

        // Get current visible filters - if empty, reset to all
        const { visibleFilters: currentVisible } = get();
        let newVisibleFilters = currentVisible;
        if (currentVisible.size === 0) {
          // First load or reset - all filters visible
          newVisibleFilters = new Set(availableFilters);
        }

        // Filter entries based on visible filters for summary
        const filteredEntries = allEntries.filter(e => {
          const filterKey = `${e.category || 'other'}:${e.type}`;
          return newVisibleFilters.size === 0 || newVisibleFilters.has(filterKey);
        });

        // Calculate summary with exchange rates (using filtered entries)
        const summary = calculateSummary(filteredEntries, rates);

        // Get overdue entries (from filtered)
        const overdueEntries = filteredEntries.filter(e => e.status === 'overdue');

        set({
          entries: allEntries,
          recurringParents,
          summary,
          overdueEntries,
          availableFilters,
          visibleFilters: newVisibleFilters,
          isLoading: false,
        });

        // Load past debts from previous months
        get().loadPastDebts();
      } catch (error) {
        console.error('Error loading cashflow entries:', error);
        set({ error: error.message, isLoading: false });
      }
    },

    loadOverdueEntries: async () => {
      try {
        const overdueEntries = await cashflow.getOverdue();
        set({ overdueEntries });
      } catch (error) {
        console.error('Error loading overdue entries:', error);
      }
    },

    /**
     * Load past debts - overdue entries from previous months
     */
    loadPastDebts: async () => {
      const { currentMonth, exchangeRates, visibleFilters } = get();
      const currentMonthStart = format(startOfMonth(currentMonth), 'yyyy-MM-dd');

      try {
        // Get all overdue entries before current month
        const allOverdue = await cashflow.getOverdue();

        // Filter to only entries from before current month AND matching visible filters
        const pastDebts = allOverdue.filter(entry => {
          const filterKey = `${entry.category || 'other'}:${entry.type}`;
          return entry.date < currentMonthStart &&
            (visibleFilters.size === 0 || visibleFilters.has(filterKey));
        });

        // Calculate totals by currency
        const pastDebtsByCurrency = {};
        let pastDebtsTotalTRY = 0;

        pastDebts.forEach(entry => {
          const amount = parseFloat(entry.amount);
          const currency = entry.currency || 'TRY';

          if (!pastDebtsByCurrency[currency]) {
            pastDebtsByCurrency[currency] = 0;
          }
          pastDebtsByCurrency[currency] += amount;

          // Convert to TRY
          pastDebtsTotalTRY += convertToTRY(amount, currency, exchangeRates);
        });

        // Calculate total in TRY (for single-currency backward compat)
        const pastDebtsTotal = pastDebtsByCurrency['TRY'] || 0;

        set({
          pastDebts,
          pastDebtsTotal,
          pastDebtsTotalTRY,
          pastDebtsByCurrency,
        });
      } catch (error) {
        console.error('Error loading past debts:', error);
      }
    },

    // =====================================================
    // AI PARSING ACTIONS
    // =====================================================

    setAICommand: (command) => set({ aiCommand: command }),

    parseCommand: async (command) => {
      set({ isParsingCommand: true, parseError: null });

      try {
        const referenceDate = new Date().toISOString();
        const parsed = await parseCashflowCommand(command, referenceDate);

        set({
          parsedEntry: { ...parsed, ai_parsed_from: command },
          isParsingCommand: false,
          isAIModalOpen: false,
          isPreviewModalOpen: true,
        });
      } catch (error) {
        console.error('Error parsing command:', error);
        set({
          parseError: error.message || 'Komut anlaşılamadı. Lütfen tekrar deneyin.',
          isParsingCommand: false,
        });
      }
    },

    confirmParsedEntry: async () => {
      const { parsedEntry } = get();
      if (!parsedEntry) return;

      try {
        // Create the entry
        const entryData = {
          title: parsedEntry.title,
          description: parsedEntry.description,
          amount: parsedEntry.amount,
          currency: parsedEntry.currency || 'TRY',
          type: parsedEntry.type,
          category: parsedEntry.category,
          account_name: parsedEntry.account_name,
          account_type: parsedEntry.account_type,
          date: parsedEntry.date,
          is_recurring: parsedEntry.is_recurring || false,
          recurring_rule: parsedEntry.recurring_rule,
          ai_parsed_from: parsedEntry.ai_parsed_from,
          status: 'pending',
        };

        await cashflow.create(entryData);

        // Reset and reload
        set({
          parsedEntry: null,
          isPreviewModalOpen: false,
          aiCommand: '',
        });

        get().loadEntries();
      } catch (error) {
        console.error('Error creating entry:', error);
        set({ parseError: error.message });
      }
    },

    // =====================================================
    // CRUD ACTIONS
    // =====================================================

    createEntry: async (entryData) => {
      try {
        await cashflow.create(entryData);
        set({ isEditModalOpen: false, selectedEntry: null });
        get().loadEntries();
      } catch (error) {
        console.error('Error creating entry:', error);
        throw error;
      }
    },

    updateEntry: async (entryId, updates) => {
      try {
        await cashflow.update(entryId, updates);
        set({ isEditModalOpen: false, selectedEntry: null });
        get().loadEntries();
      } catch (error) {
        console.error('Error updating entry:', error);
        throw error;
      }
    },

    deleteEntry: async (entryId) => {
      try {
        await cashflow.delete(entryId);
        set({ isEditModalOpen: false, selectedEntry: null });
        get().loadEntries();
      } catch (error) {
        console.error('Error deleting entry:', error);
        throw error;
      }
    },

    /**
     * Delete an entire loan group
     */
    deleteLoanGroup: async (groupId) => {
      try {
        await cashflow.deleteLoanGroup(groupId);
        set({ isEditModalOpen: false, selectedEntry: null });
        get().loadEntries();
      } catch (error) {
        console.error('Error deleting loan group:', error);
        throw error;
      }
    },

    /**
     * Check if entry is part of a loan group
     */
    checkLoanGroup: async (entryId) => {
      try {
        return await cashflow.isPartOfLoanGroup(entryId);
      } catch (error) {
        console.error('Error checking loan group:', error);
        return { isGrouped: false };
      }
    },

    /**
     * Update shared fields across all entries in a loan group
     */
    updateLoanGroupSharedFields: async (groupId, fields) => {
      try {
        await cashflow.updateLoanGroupSharedFields(groupId, fields);
        get().loadEntries();
      } catch (error) {
        console.error('Error updating loan group fields:', error);
        throw error;
      }
    },

    // =====================================================
    // STATUS ACTIONS
    // =====================================================

    toggleCompleted: async (entry) => {
      // Handle virtual entries - create real entry first
      if (entry.is_virtual) {
        try {
          const newEntry = await cashflow.createRecurringInstance(
            entry.recurring_parent_id,
            entry.date
          );
          await cashflow.markCompleted(newEntry.id);
          get().loadEntries();
        } catch (error) {
          console.error('Error completing virtual entry:', error);
        }
        return;
      }

      try {
        if (entry.status === 'completed') {
          await cashflow.markPending(entry.id);
        } else {
          await cashflow.markCompleted(entry.id);
        }
        get().loadEntries();
      } catch (error) {
        console.error('Error toggling entry status:', error);
      }
    },

    rescheduleEntry: async (entryId, newDate) => {
      try {
        await cashflow.reschedule(entryId, newDate);
        get().loadEntries();
      } catch (error) {
        console.error('Error rescheduling entry:', error);
        throw error;
      }
    },

    // =====================================================
    // MODAL ACTIONS
    // =====================================================

    openAIModal: () => set({ isAIModalOpen: true, parseError: null, aiCommand: '' }),
    closeAIModal: () => set({ isAIModalOpen: false, parseError: null, aiCommand: '' }),

    openEditModal: (entry = null) => set({
      isEditModalOpen: true,
      selectedEntry: entry,
    }),
    closeEditModal: () => set({ isEditModalOpen: false, selectedEntry: null }),

    openPreviewModal: () => set({ isPreviewModalOpen: true }),
    closePreviewModal: () => set({
      isPreviewModalOpen: false,
      parsedEntry: null,
    }),

    editParsedEntry: () => {
      const { parsedEntry } = get();
      set({
        isPreviewModalOpen: false,
        isEditModalOpen: true,
        selectedEntry: parsedEntry,
      });
    },

    toggleOverduePanel: () => set(state => ({
      isOverduePanelOpen: !state.isOverduePanelOpen,
    })),

    closeOverduePanel: () => set({ isOverduePanelOpen: false }),

    // =====================================================
    // PAYMENT SCHEDULE IMPORT ACTIONS
    // =====================================================

    openScheduleModal: () => set({
      isScheduleModalOpen: true,
      scheduleParseError: null,
      parsedSchedule: null,
      selectedPayments: [],
    }),

    closeScheduleModal: () => set({
      isScheduleModalOpen: false,
      parsedSchedule: null,
      scheduleParseError: null,
      selectedPayments: [],
      isParsingSchedule: false,
    }),

    parseScheduleFile: async (file) => {
      set({ isParsingSchedule: true, scheduleParseError: null, parsedSchedule: null });

      try {
        let result;

        // Handle image files with Vision AI
        if (isImage(file)) {
          const imageBase64 = await fileToBase64(file);
          result = await parsePaymentScheduleFromImage(imageBase64, file.type, file.name);
        } else {
          // Extract text based on file type
          let content = '';
          let fileType = 'unknown';

          if (isPDF(file)) {
            const extracted = await extractTextFromPDF(file);
            content = extracted.text;
            fileType = 'pdf';
          } else if (isExcel(file)) {
            const extracted = await extractTextFromExcel(file);
            content = extracted.text;
            fileType = 'excel';
          } else {
            throw new Error('Desteklenmeyen dosya formatı. PDF, Excel veya görsel (PNG/JPG) dosyası yükleyin.');
          }

          if (!content || content.trim().length < 50) {
            throw new Error('Dosyadan yeterli metin çıkarılamadı. Dosyanın metin içerdiğinden emin olun.');
          }

          // Parse with AI
          result = await parsePaymentSchedule(content, file.name, fileType);
        }

        if (!result.payments || result.payments.length === 0) {
          throw new Error(result.warnings?.[0] || 'Dosyadan ödeme bilgisi çıkarılamadı.');
        }

        // Add temporary IDs for selection
        const paymentsWithIds = result.payments.map((p, idx) => ({
          ...p,
          tempId: `temp_${idx}_${Date.now()}`,
        }));

        // Select future payments by default
        const today = new Date().toISOString().split('T')[0];
        const futurePaymentIds = paymentsWithIds
          .filter(p => p.due_date >= today)
          .map(p => p.tempId);

        set({
          parsedSchedule: { ...result, payments: paymentsWithIds },
          selectedPayments: futurePaymentIds,
          isParsingSchedule: false,
        });
      } catch (error) {
        console.error('Error parsing schedule file:', error);
        set({
          scheduleParseError: error.message,
          isParsingSchedule: false,
        });
      }
    },

    togglePaymentSelection: (tempId) => {
      set(state => {
        const isSelected = state.selectedPayments.includes(tempId);
        return {
          selectedPayments: isSelected
            ? state.selectedPayments.filter(id => id !== tempId)
            : [...state.selectedPayments, tempId],
        };
      });
    },

    selectAllPayments: () => {
      const { parsedSchedule } = get();
      if (!parsedSchedule?.payments) return;
      set({ selectedPayments: parsedSchedule.payments.map(p => p.tempId) });
    },

    selectFuturePayments: () => {
      const { parsedSchedule } = get();
      if (!parsedSchedule?.payments) return;
      const today = new Date().toISOString().split('T')[0];
      const futureIds = parsedSchedule.payments
        .filter(p => p.due_date >= today)
        .map(p => p.tempId);
      set({ selectedPayments: futureIds });
    },

    deselectAllPayments: () => set({ selectedPayments: [] }),

    importSelectedPayments: async (settings = {}) => {
      const { parsedSchedule, selectedPayments } = get();
      if (!parsedSchedule?.payments || selectedPayments.length === 0) return;

      const { accountName = '', accountType = '', markPastAsPaid = false } = settings;
      const today = new Date().toISOString().split('T')[0];

      set({ isParsingSchedule: true });

      try {
        const paymentsToImport = parsedSchedule.payments
          .filter(p => selectedPayments.includes(p.tempId))
          .sort((a, b) => new Date(a.due_date) - new Date(b.due_date)); // Sort by date

        // Check if this looks like a loan/installment plan (multiple payments with installment numbers)
        const hasInstallments = paymentsToImport.some(p => p.installment_number || p.total_installments);
        const isLoanSchedule = parsedSchedule.documentType === 'credit_schedule' ||
                               parsedSchedule.documentType === 'payment_plan' ||
                               hasInstallments;

        if (isLoanSchedule && paymentsToImport.length > 1) {
          // Create as a loan group (all installments linked together)
          const loanInfo = {
            creditor: parsedSchedule.creditor || null,
            currency: parsedSchedule.currency || 'TRY',
            totalAmount: parsedSchedule.totalAmount || null,
            loan_type: parsedSchedule.metadata?.loan_type || null,
            interest_rate: parsedSchedule.metadata?.interest_rate || null,
            source: 'file_import',
            account_name: accountName || null,
            account_type: accountType || null,
          };

          // Prepare installments with proper numbering and status
          const installments = paymentsToImport.map((payment, idx) => {
            const isPast = payment.due_date < today;
            return {
              title: payment.title || `${parsedSchedule.creditor || 'Kredi'} Taksit #${idx + 1}`,
              description: payment.description || null,
              amount: payment.amount,
              currency: payment.currency || parsedSchedule.currency || 'TRY',
              due_date: payment.due_date,
              principal: payment.principal || null,
              interest: payment.interest || null,
              status: (markPastAsPaid && isPast) ? 'completed' : 'pending',
            };
          });

          await cashflow.createLoanGroup(loanInfo, installments);
        } else {
          // Create as individual entries (single payment or non-loan items)
          for (const payment of paymentsToImport) {
            const isPast = payment.due_date < today;
            const entryData = {
              title: payment.title,
              description: payment.description || null,
              amount: payment.amount,
              currency: payment.currency || parsedSchedule.currency || 'TRY',
              type: payment.type || 'expense',
              category: payment.category || 'bill',
              date: payment.due_date,
              is_recurring: false,
              status: (markPastAsPaid && isPast) ? 'completed' : 'pending',
              creditor: parsedSchedule.creditor || null,
              account_name: accountName || null,
              account_type: accountType || null,
            };

            await cashflow.create(entryData);
          }
        }

        // Close modal and reload
        set({
          isScheduleModalOpen: false,
          parsedSchedule: null,
          selectedPayments: [],
          isParsingSchedule: false,
        });

        get().loadEntries();
      } catch (error) {
        console.error('Error importing payments:', error);
        set({
          scheduleParseError: `İçe aktarma hatası: ${error.message}`,
          isParsingSchedule: false,
        });
      }
    },

    // =====================================================
    // CATEGORY FILTER ACTIONS
    // =====================================================

    toggleFilter: (filterKey) => {
      const { visibleFilters, entries, exchangeRates } = get();
      const newVisible = new Set(visibleFilters);

      if (newVisible.has(filterKey)) {
        newVisible.delete(filterKey);
      } else {
        newVisible.add(filterKey);
      }

      // Recalculate summary with filtered entries
      const filteredEntries = entries.filter(e => {
        const key = `${e.category || 'other'}:${e.type}`;
        return newVisible.size === 0 || newVisible.has(key);
      });
      const summary = calculateSummary(filteredEntries, exchangeRates);
      const overdueEntries = filteredEntries.filter(e => e.status === 'overdue');

      set({ visibleFilters: newVisible, summary, overdueEntries });

      // Also update past debts
      get().loadPastDebts();
    },

    selectAllFilters: () => {
      const { availableFilters, entries, exchangeRates } = get();
      const newVisible = new Set(availableFilters);

      const filteredEntries = entries.filter(e => {
        const key = `${e.category || 'other'}:${e.type}`;
        return newVisible.has(key);
      });
      const summary = calculateSummary(filteredEntries, exchangeRates);
      const overdueEntries = filteredEntries.filter(e => e.status === 'overdue');

      set({ visibleFilters: newVisible, summary, overdueEntries });
      get().loadPastDebts();
    },

    deselectAllFilters: () => {
      const { exchangeRates } = get();
      const summary = calculateSummary([], exchangeRates);

      set({
        visibleFilters: new Set(),
        summary,
        overdueEntries: [],
      });
      get().loadPastDebts();
    },

    isFilterVisible: (filterKey) => {
      const { visibleFilters } = get();
      return visibleFilters.size === 0 || visibleFilters.has(filterKey);
    },

    // =====================================================
    // CREDIT CARD STATEMENT ACTIONS
    // =====================================================

    openCreditCardModal: () => set({
      isCreditCardModalOpen: true,
      statementParseError: null,
      parsedStatement: null,
      selectedTransactions: [],
    }),

    closeCreditCardModal: () => set({
      isCreditCardModalOpen: false,
      parsedStatement: null,
      statementParseError: null,
      selectedTransactions: [],
      isParsingStatement: false,
    }),

    openCreditCardSidebar: () => {
      set({ isCreditCardSidebarOpen: true });
      // Load data if not loaded
      const { creditCardStatements } = get();
      if (creditCardStatements.length === 0) {
        get().loadCreditCardData();
      }
    },

    closeCreditCardSidebar: () => set({ isCreditCardSidebarOpen: false }),

    parseStatementFile: async (file) => {
      set({ isParsingStatement: true, statementParseError: null, parsedStatement: null });

      try {
        let result;

        // Handle image files with Vision AI
        if (isImage(file)) {
          const imageBase64 = await fileToBase64(file);
          result = await parseCreditCardStatementFromImage(imageBase64, file.type, file.name);
        } else {
          // Extract text based on file type
          let content = '';
          let fileType = 'unknown';

          if (isPDF(file)) {
            const extracted = await extractTextFromPDF(file);
            content = extracted.text;
            fileType = 'pdf';
          } else if (isExcel(file)) {
            const extracted = await extractTextFromExcel(file);
            content = extracted.text;
            fileType = 'excel';
          } else {
            throw new Error('Desteklenmeyen dosya formatı. PDF, Excel veya görsel (PNG/JPG) dosyası yükleyin.');
          }

          if (!content || content.trim().length < 50) {
            throw new Error('Dosyadan yeterli metin çıkarılamadı. Dosyanın metin içerdiğinden emin olun.');
          }

          // Parse with AI
          result = await parseCreditCardStatement(content, file.name, fileType);
        }

        if (!result.transactions || result.transactions.length === 0) {
          throw new Error(result.warnings?.[0] || 'Dosyadan işlem bilgisi çıkarılamadı.');
        }

        // Add temporary IDs for selection
        const transactionsWithIds = result.transactions.map((t, idx) => ({
          ...t,
          tempId: `temp_${idx}_${Date.now()}`,
        }));

        // Select all transactions by default
        const allTransactionIds = transactionsWithIds.map(t => t.tempId);

        set({
          parsedStatement: { ...result, transactions: transactionsWithIds },
          selectedTransactions: allTransactionIds,
          isParsingStatement: false,
        });
      } catch (error) {
        console.error('Error parsing statement file:', error);
        set({
          statementParseError: error.message,
          isParsingStatement: false,
        });
      }
    },

    toggleTransactionSelection: (tempId) => {
      set(state => {
        const isSelected = state.selectedTransactions.includes(tempId);
        return {
          selectedTransactions: isSelected
            ? state.selectedTransactions.filter(id => id !== tempId)
            : [...state.selectedTransactions, tempId],
        };
      });
    },

    selectAllTransactions: () => {
      const { parsedStatement } = get();
      if (!parsedStatement?.transactions) return;
      set({ selectedTransactions: parsedStatement.transactions.map(t => t.tempId) });
    },

    deselectAllTransactions: () => set({ selectedTransactions: [] }),

    importStatement: async () => {
      const { parsedStatement, selectedTransactions } = get();
      if (!parsedStatement) return;

      set({ isParsingStatement: true });

      try {
        const { cardInfo, statementPeriod, summary } = parsedStatement;

        // Check for duplicate statement
        const existingStatement = await creditCards.checkDuplicate({
          cardNumberMasked: cardInfo?.cardNumberMasked,
          periodStart: statementPeriod?.startDate,
          periodEnd: statementPeriod?.endDate,
          dueDate: statementPeriod?.dueDate,
          totalBalance: summary?.totalBalance,
        });

        if (existingStatement) {
          const existingDate = existingStatement.created_at
            ? new Date(existingStatement.created_at).toLocaleDateString('tr-TR')
            : '';
          throw new Error(
            `Bu ekstre daha önce yüklenmiş görünüyor.\n` +
            `Banka: ${existingStatement.bank_name || 'Bilinmiyor'}\n` +
            `Dönem: ${existingStatement.statement_period_start} - ${existingStatement.statement_period_end}\n` +
            `Yüklenme tarihi: ${existingDate}`
          );
        }

        // Filter selected transactions
        const transactionsToImport = parsedStatement.transactions
          .filter(t => selectedTransactions.includes(t.tempId))
          .map(({ tempId, ...rest }) => rest); // Remove tempId before saving

        // Prepare statement data
        const statementData = {
          card_number_masked: cardInfo?.cardNumberMasked || 'XXXX',
          card_name: cardInfo?.cardName || null,
          bank_name: cardInfo?.bankName || null,
          statement_period_start: statementPeriod?.startDate || null,
          statement_period_end: statementPeriod?.endDate || null,
          due_date: statementPeriod?.dueDate || new Date().toISOString().split('T')[0],
          minimum_payment: summary?.minimumPayment || null,
          total_balance: summary?.totalBalance || 0,
          previous_balance: summary?.previousBalance || null,
          payments_received: summary?.paymentsReceived || null,
          new_charges: summary?.newCharges || null,
          interest_charged: summary?.interestCharged || null,
          currency: summary?.currency || 'TRY',
          source_file_name: parsedStatement.sourceFileName || null,
          source_file_type: parsedStatement.sourceFileType || null,
          ai_confidence: parsedStatement.confidence || null,
          ai_raw_response: parsedStatement,
        };

        // Create statement with transactions
        const savedStatement = await creditCards.createStatement(statementData, transactionsToImport);

        // Also create a cashflow entry for the due date
        // Store both minimum and full amounts in metadata for toggle feature
        if (savedStatement && summary?.totalBalance > 0) {
          const { creditCardPaymentMode } = get();
          const minimumPayment = summary.minimumPayment || summary.totalBalance;
          const fullBalance = summary.totalBalance;

          // Use the amount based on current mode
          const displayAmount = creditCardPaymentMode === 'minimum' ? minimumPayment : fullBalance;

          const cashflowEntry = {
            title: `${cardInfo?.bankName || 'Kredi Kartı'} ${cardInfo?.cardNumberMasked?.slice(-4) || ''} Ekstre`,
            description: `${statementPeriod?.startDate} - ${statementPeriod?.endDate} dönemi`,
            amount: displayAmount,
            currency: summary.currency || 'TRY',
            type: 'expense',
            category: 'credit_card',
            date: statementPeriod?.dueDate || new Date().toISOString().split('T')[0],
            status: 'pending',
            metadata: {
              statement_id: savedStatement.id,
              minimum_payment: minimumPayment,
              full_balance: fullBalance,
              is_credit_card_payment: true,
            },
          };

          await cashflow.create(cashflowEntry);
        }

        // Close modal and reload
        set({
          isCreditCardModalOpen: false,
          parsedStatement: null,
          selectedTransactions: [],
          isParsingStatement: false,
        });

        // Reload data
        get().loadEntries();
        get().loadCreditCardData();
      } catch (error) {
        console.error('Error importing statement:', error);
        set({
          statementParseError: `İçe aktarma hatası: ${error.message}`,
          isParsingStatement: false,
        });
      }
    },

    loadCreditCardData: async () => {
      set({ isLoadingCreditCards: true });

      try {
        const statements = await creditCards.getStatements();

        // Load ALL transactions for all statements (no date filter)
        // This ensures we can calculate payments made for each statement
        const transactions = await creditCards.getTransactions({
          limit: 2000, // Get all transactions
        });

        set({
          creditCardStatements: statements,
          creditCardTransactions: transactions,
          isLoadingCreditCards: false,
        });
      } catch (error) {
        console.error('Error loading credit card data:', error);
        set({ isLoadingCreditCards: false });
      }
    },

    // =====================================================
    // SPENDING ANALYSIS ACTIONS
    // =====================================================

    openSpendingAnalysis: async () => {
      set({ isSpendingAnalysisOpen: true, spendingAnalysisError: null });

      // Load credit card data if not loaded
      const { creditCardStatements, spendingAnalysis } = get();
      if (creditCardStatements.length === 0) {
        get().loadCreditCardData();
      }

      // Try to load cached analysis from DB if we don't have one in memory
      if (!spendingAnalysis) {
        get().loadCachedAnalysis();
      }
    },

    closeSpendingAnalysis: () => set({
      isSpendingAnalysisOpen: false,
      spendingAnalysisError: null,
    }),

    /**
     * Load cached analysis from database
     */
    loadCachedAnalysis: async () => {
      set({ isLoadingCachedAnalysis: true });

      try {
        const cached = await spendingAnalysis.getLatest('credit_card');

        if (cached) {
          set({
            spendingAnalysis: cached.analysis_result,
            spendingAnalysisId: cached.id,
            spendingAnalysisUpdatedAt: cached.updated_at,
            isLoadingCachedAnalysis: false,
          });
        } else {
          set({ isLoadingCachedAnalysis: false });
        }
      } catch (error) {
        console.error('Error loading cached analysis:', error);
        set({ isLoadingCachedAnalysis: false });
      }
    },

    /**
     * Run spending analysis (with optional force refresh)
     * @param {boolean} forceRefresh - If true, always runs new analysis
     */
    runSpendingAnalysis: async (forceRefresh = false) => {
      const { creditCardStatements, creditCardTransactions, spendingAnalysis: existingAnalysis, spendingAnalysisUpdatedAt } = get();

      // If we have a recent cached analysis and not forcing refresh, skip
      if (!forceRefresh && existingAnalysis && spendingAnalysisUpdatedAt) {
        const hoursSinceUpdate = (new Date() - new Date(spendingAnalysisUpdatedAt)) / (1000 * 60 * 60);
        if (hoursSinceUpdate < 24) {
          console.log('Using cached analysis (less than 24h old)');
          return;
        }
      }

      if (creditCardTransactions.length === 0) {
        set({ spendingAnalysisError: 'Analiz için yeterli işlem verisi bulunamadı. Önce bir kredi kartı ekstresi yükleyin.' });
        return;
      }

      set({ isAnalyzingSpending: true, spendingAnalysisError: null });

      try {
        // Calculate date range from transactions
        const sortedDates = creditCardTransactions
          .map(t => t.transaction_date)
          .filter(Boolean)
          .sort();
        const periodStart = sortedDates[0] || new Date().toISOString().split('T')[0];
        const periodEnd = sortedDates[sortedDates.length - 1] || new Date().toISOString().split('T')[0];

        // Prepare data for analysis
        const analysisData = {
          statements: creditCardStatements.map(s => ({
            id: s.id,
            cardNumberMasked: s.card_number_masked,
            bankName: s.bank_name,
            periodStart: s.statement_period_start,
            periodEnd: s.statement_period_end,
            dueDate: s.due_date,
            totalBalance: s.total_balance,
            currency: s.currency,
          })),
          transactions: creditCardTransactions.map(t => ({
            date: t.transaction_date,
            merchantName: t.merchant_name,
            amount: t.amount,
            currency: t.currency,
            category: t.ai_category || t.user_category,
            subcategory: t.ai_subcategory,
            transactionType: t.transaction_type,
            isInstallment: t.is_installment,
          })),
          dateRange: {
            start: periodStart,
            end: periodEnd,
          },
        };

        const analysis = await analyzeSpending(analysisData);

        // Save to database
        const savedAnalysis = await spendingAnalysis.save({
          periodStart,
          periodEnd,
          analysisType: 'credit_card',
          statementsAnalyzed: creditCardStatements.length,
          transactionsAnalyzed: creditCardTransactions.length,
          analysisResult: analysis,
        });

        set({
          spendingAnalysis: analysis,
          spendingAnalysisId: savedAnalysis.id,
          spendingAnalysisUpdatedAt: savedAnalysis.updated_at,
          isAnalyzingSpending: false,
        });
      } catch (error) {
        console.error('Error running spending analysis:', error);
        set({
          spendingAnalysisError: error.message,
          isAnalyzingSpending: false,
        });
      }
    },

    updateTransactionCategory: async (transactionId, category) => {
      try {
        await creditCards.updateTransactionCategory(transactionId, category);
        get().loadCreditCardData();
      } catch (error) {
        console.error('Error updating transaction category:', error);
      }
    },

    /**
     * Toggle credit card payment mode between minimum and full balance
     */
    toggleCreditCardPaymentMode: () => {
      const { creditCardPaymentMode } = get();
      const newMode = creditCardPaymentMode === 'minimum' ? 'full' : 'minimum';
      set({ creditCardPaymentMode: newMode });

      // Recalculate summary with new mode
      get().loadEntries();
    },

    setCreditCardPaymentMode: (mode) => {
      set({ creditCardPaymentMode: mode });
      get().loadEntries();
    },

    deleteStatement: async (statementId) => {
      try {
        await creditCards.deleteStatement(statementId);
        get().loadCreditCardData();
        get().loadEntries(); // Reload cashflow entries too
      } catch (error) {
        console.error('Error deleting statement:', error);
        throw error;
      }
    },

    // =====================================================
    // HELPER GETTERS
    // =====================================================

    getEntriesForDate: (date) => {
      const { entries, visibleFilters } = get();
      const dateStr = format(date, 'yyyy-MM-dd');
      return entries.filter(e => {
        const filterKey = `${e.category || 'other'}:${e.type}`;
        return e.date === dateStr &&
          (visibleFilters.size === 0 || visibleFilters.has(filterKey));
      });
    },

    getEntriesByType: (type) => {
      const { entries, visibleFilters } = get();
      return entries.filter(e => {
        const filterKey = `${e.category || 'other'}:${e.type}`;
        return e.type === type &&
          (visibleFilters.size === 0 || visibleFilters.has(filterKey));
      });
    },

    getFilteredEntries: () => {
      const { entries, visibleFilters } = get();
      return entries.filter(e => {
        const filterKey = `${e.category || 'other'}:${e.type}`;
        return visibleFilters.size === 0 || visibleFilters.has(filterKey);
      });
    },
  }))
);

// Helper: Calculate summary from entries
function calculateSummary(entries, exchangeRates = null) {
  const summary = {
    totalIncome: 0,
    totalExpense: 0,
    netAmount: 0,
    pendingCount: 0,
    overdueCount: 0,
    completedCount: 0,
    byCurrency: {},
    // TRY equivalents (all currencies converted)
    totalIncomeTRY: 0,
    totalExpenseTRY: 0,
    netAmountTRY: 0,
  };

  entries.forEach(entry => {
    const amount = parseFloat(entry.amount);
    const currency = entry.currency || 'TRY';

    // Initialize currency bucket
    if (!summary.byCurrency[currency]) {
      summary.byCurrency[currency] = { income: 0, expense: 0, net: 0 };
    }

    // Convert to TRY for total calculations
    const amountInTRY = convertToTRY(amount, currency, exchangeRates);

    if (entry.type === 'income') {
      summary.byCurrency[currency].income += amount;
      summary.totalIncomeTRY += amountInTRY;
    } else {
      summary.byCurrency[currency].expense += amount;
      summary.totalExpenseTRY += amountInTRY;
    }

    summary.byCurrency[currency].net =
      summary.byCurrency[currency].income - summary.byCurrency[currency].expense;

    // Status counts
    if (entry.status === 'pending') {
      summary.pendingCount++;
    } else if (entry.status === 'completed') {
      summary.completedCount++;
    } else if (entry.status === 'overdue') {
      summary.overdueCount++;
    }
  });

  // Calculate net in TRY
  summary.netAmountTRY = summary.totalIncomeTRY - summary.totalExpenseTRY;

  // Also keep TRY-only totals for backward compatibility
  if (summary.byCurrency['TRY']) {
    summary.totalIncome = summary.byCurrency['TRY'].income;
    summary.totalExpense = summary.byCurrency['TRY'].expense;
    summary.netAmount = summary.byCurrency['TRY'].net;
  }

  return summary;
}

export default useCashflowStore;
